import { useEffect, useRef, useState } from "react";
import { ArrowUpIcon, StopIcon } from "@heroicons/react/24/solid";
import { QuinnBusinessOption, QuinnCopy, QuinnScope, QuinnScopeMode } from "../../types";
import { scopeIsAnswerable } from "../../utils/constants.ts";
import { QuinnScopeChips } from "../Scope";

type QuinnComposerProps = {
    scope: QuinnScope;
    businesses: QuinnBusinessOption[];
    isBusy: boolean;
    isHydrating: boolean;
    canAskAllClients: boolean;
    draft: string;
    onDraftChange: (draft: string) => void;
    onScopeChange: (patch: Partial<QuinnScope>) => void;
    onSubmit: (content: string) => void;
    onStop: () => void;
};

const MAX_ROWS_HEIGHT = 148;

export function QuinnComposer({
    scope,
    businesses,
    isBusy,
    isHydrating,
    canAskAllClients,
    draft,
    onDraftChange,
    onScopeChange,
    onSubmit,
    onStop,
}: QuinnComposerProps) {
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);
    const [isFocused, setIsFocused] = useState(false);

    useEffect(() => {
        const textarea = textareaRef.current;
        if (!textarea) return;
        textarea.style.height = "auto";
        textarea.style.height = `${Math.min(textarea.scrollHeight, MAX_ROWS_HEIGHT)}px`;
    }, [draft]);

    function submit() {
        const trimmed = draft.trim();
        if (!trimmed || isBusy || isHydrating || !scopeIsAnswerable(scope)) return;
        onSubmit(trimmed);
        onDraftChange("");
    }

    const canSubmit = Boolean(draft.trim() && scopeIsAnswerable(scope) && !isBusy && !isHydrating);

    return (
        <div className="border-t border-gray-200 bg-white px-4 pb-4 pt-3">
            <div className="mb-2">
                <QuinnScopeChips
                    canAskAllClients={canAskAllClients}
                    scope={scope}
                    businesses={businesses}
                    isLoading={isHydrating}
                    onChange={onScopeChange}
                />
            </div>

            <form
                onSubmit={(event) => {
                    event.preventDefault();
                    submit();
                }}
                className={`rounded-2xl border bg-gray-50/80 px-3 py-2 transition-colors ${
                    isFocused ? "border-primary/50 bg-white" : "border-gray-200"
                }`}
            >
                <textarea
                    ref={textareaRef}
                    rows={1}
                    value={draft}
                    onChange={(event) => onDraftChange(event.target.value)}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                    onKeyDown={(event) => {
                        if (event.key === "Enter" && !event.shiftKey) {
                            event.preventDefault();
                            submit();
                        }
                    }}
                    disabled={isHydrating}
                    placeholder={
                        isHydrating
                            ? QuinnCopy.LoadingClients
                            : scope.mode === QuinnScopeMode.AllClients
                              ? "Vraag Quinn iets over al je klanten…"
                              : scope.businessId
                                ? "Vraag Quinn iets over deze klant…"
                                : "Kies eerst een klant om Quinn te vragen…"
                    }
                    className="max-h-[148px] w-full resize-none border-0 bg-transparent p-0 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-0"
                />

                <div className="mt-2 flex items-center justify-between">
                    <span className="text-[10px] text-gray-400">
                        {isHydrating
                            ? QuinnCopy.LoadingClients
                            : scopeIsAnswerable(scope)
                              ? "⇧ + ↵ voor een nieuwe regel"
                              : "Selecteer een klant hierboven"}
                    </span>
                    {isBusy ? (
                        <button
                            type="button"
                            onClick={onStop}
                            className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-gray-300 text-gray-500 transition-colors hover:bg-gray-100"
                            aria-label="Stoppen"
                        >
                            <StopIcon className="h-3 w-3" />
                        </button>
                    ) : (
                        <button
                            type="submit"
                            disabled={!canSubmit}
                            className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-primary text-white shadow-sm transition-opacity hover:opacity-90 disabled:opacity-30"
                            aria-label="Vraag versturen"
                        >
                            <ArrowUpIcon className="h-3.5 w-3.5" />
                        </button>
                    )}
                </div>
            </form>
        </div>
    );
}
